"use client";
import * as React from "react";
import { useContext } from "react";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import SelectedExper from "./SelectedExper";
import { Controller, useFormContext } from "react-hook-form";
import ExperienceContext from "@/context/experienceContext";
import SelectedExperContext from "@/context/selectedExperContext";
import UserContext from "@/context/userContext";
import SurveyDataContext from "@/context/surveyDataContext";
import SurveyContext from "@/context/surveyContext";
import { ExperienceObj } from "@/app/@types/experienceobj";
import CustomExperContext from "@/context/customExperContext";
import SubmittedExperContext from "@/context/submittedExperContext";
import VideoIntroContext from "@/context/videoIntroContext";
import CategoryPanelInner from "./CategoryPanelInner";
import NextMonthYear from "./NextMonthYear";
import { Box, styled } from "@mui/material";

/**
 * A React component to display the YEX intro video.
 *
 * @remarks
 * This component displays YEX Intro Video. It uses the React
 * Context API to reset the selected experiences, the user ID, and selected
 * experiences back to null so that the sign in page is displayed once the
 * "Sign Out" button is pressed.  This component is a parent component to the
 * SelectedExper component that holds the individual experience(s).
 *
 * @param props - React props not currently used.
 * @returns - The SelectedExperPanel React component in JSX.
 */

export default function VideoIntroPanel(props: any) {
  // const { setUser } = useContext(UserContext);
  const { setVideoIntro } = useContext(VideoIntroContext);

  return (
    <Stack
      spacing={1}
      alignItems="center"
      sx={{ mt: 2, bgcolor: "secondary.dark" }}
    >
      <Paper
        sx={{
          position: "relative",
          bgcolor: "secondary.dark",
          color: "secondary.light",
        }}
        elevation={0}
        square={false}
      >
        <Stack spacing={1}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center", // Centers the video horizontally
              width: "100%", // Ensures the Box spans the full width of the parent
            }}
          >
            <video controls autoPlay width="1080" height="607" preload="auto">
              <source
                src="https://yex.center/assets/media/yex_720.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </Box>

          <Paper
            sx={{
              padding: 3,
              color: "secondary.light",
              bgcolor: "secondary.dark",
            }}
            elevation={0}
          >
            <Container sx={{ marginTop: 10 }}>
              <Stack spacing={10} sx={{ marginBottom: 10 }}>
                <Stack
                  direction="row"
                  spacing={5}
                  alignItems="flex-start"
                  justifyContent="flex-end"
                  sx={{ width: "100%" }}
                >
                  <Typography variant="h1">
                    Welcome to{" "}
                    <Typography
                      variant="inherit"
                      component="span"
                      color="primary"
                      sx={{ fontWeight: "bold" }}
                    >
                      YEX
                    </Typography>
                  </Typography>
                  <Stack>
                    <Typography variant="h5" sx={{ ml: 10 }}>
                      You EXpanded (YEX)
                    </Typography>
                    <Typography variant="h5" sx={{ mr: 25 }}>
                      Convenient self-optimization for your mind and body that
                      can help you to... BE IN FLOW. YEX offers an immersive
                      on-site experience using sight, sound, touch, and smell,
                      to connect with the best version of you. When you need to
                      be energized or need a reset, we have engaging products
                      with knowledgeable staff to get you there.
                    </Typography>
                  </Stack>
                </Stack>
                <Stack alignItems="center" spacing={5}>
                  <Button
                    variant="contained"
                    sx={{ fontSize: "3rem" }}
                    onClick={() => {
                      setVideoIntro(true);
                    }}
                  >
                    Continue to the YEX Survey
                  </Button>
                </Stack>
              </Stack>
            </Container>
          </Paper>
        </Stack>
        <Stack direction="row" spacing={5} sx={{ marginBottom: 15 }}>
          <Typography variant="h2">DEEP ON</Typography>
          <Typography variant="h5">
            Deep On experiences are movement-based, physical, and interactive.
            They’re designed to get your blood flowing and heighten your senses,
            helping you connect with the best version of yourself through
            dynamic action.
          </Typography>
        </Stack>
        <CategoryPanelInner categoryPanelType="deepOn" />
        <Stack
          direction="row"
          spacing={5}
          sx={{ marginTop: 15, marginBottom: 15 }}
        >
          <Typography variant="h2">DEEP OFF</Typography>
          <Typography variant="h5">
            Deep Off experiences are calm, reflective, and help you feel more in
            tune with yourself. Designed to help you disconnect from the noise
            and go inward — giving you space to recharge, reflect, and refocus.
          </Typography>
        </Stack>
        <Box sx={{ zIndex: 100 }}>
          <CategoryPanelInner categoryPanelType="deepOff" />
        </Box>
        <Stack
          sx={{
            position: "relative",
            marginTop: 15,
          }}
        >
          <NextMonthYear />
          <CategoryPanelInner categoryPanelType="comingNextMonth" />
        </Stack>
      </Paper>
    </Stack>
  );
}
